import styled from "@emotion/styled";
import { useState } from "react";

import getAuthClient from "@skilitics/designer-auth";
import { useUserAttributes } from "@skilitics/designer-auth/attributes";
import { logout } from "@skilitics/designer-auth/login";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FieldLabel,
  FieldText,
  Form,
  FormField,
  FormikForm,
  FormValidationErrorMessages,
  ModalDialog,
  PrimaryButton,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

export interface RemoveAccountDialogProps {
  close: () => void;
}

type FormValues = {
  confirm: string;
};

const initialValues: FormValues = {
  confirm: "",
};

export const RemoveUserDialog = ({ close }: RemoveAccountDialogProps) => {
  const { email } = useUserAttributes() ?? {};
  const formatMessage = useFormatMessage(formatStrings);

  // Formik handles pending state, but not errors from submit?
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (values: FormValues) => {
    if (values.confirm.trim() !== email) {
      return {
        confirm: formatMessage("confirmError", { email }),
      };
    }
  };

  const submit = async () => {
    setSubmitError(null);
    try {
      await getAuthClient().removeSelf();
      await logout();
    } catch (err) {
      setSubmitError(String(err));
    }
  };

  return (
    <ModalDialog open onClose={close}>
      <FormikForm
        initialValues={initialValues}
        formatStrings={formatStrings}
        validate={validate}
        validateOnChange={false}
        validateOnBlur={false}
        submitOnBlur={false}
        onSubmit={submit}
      >
        {({ isSubmitting, errors }) => (
          <Form blurOnEnter={false} onChange={() => setSubmitError(null)}>
            <DialogTitle>{formatMessage("header")}</DialogTitle>
            <StyledDialogContent dividers>
              <Description1>{formatMessage("description1")}</Description1>
              <Description2>{formatMessage("description2")}</Description2>
              <Description3>{formatMessage("description3", { email })}</Description3>
              <ConfirmSection>
                <FormField name="confirm" explicitLabel>
                  <FieldLabel />
                  <FieldText fullWidth placeholder={formatMessage("confirmPlaceholder", { email })} />
                </FormField>
              </ConfirmSection>
              <FormValidationErrorMessages
                errors={[...(submitError === null ? [] : [submitError]), ...Object.values(errors)]}
              />
            </StyledDialogContent>
            <DialogActions>
              <Button disabled={isSubmitting} type="button" onClick={close}>
                {formatMessage("cancelLabel")}
              </Button>
              <Spacer />
              <PrimaryButton disabled={isSubmitting} type="submit" color="error">
                {formatMessage("removeLabel")}
              </PrimaryButton>
            </DialogActions>
          </Form>
        )}
      </FormikForm>
    </ModalDialog>
  );
};

const StyledDialogContent = styled(DialogContent)`
  width: 450px;
  display: flex;
  flex-flow: column;
  gap: 12px;
`;

const Description1 = styled.p`
  margin: 0;
  font-weight: 600;
  color: ${({ theme }) => theme.error};
`;

const Description2 = styled.p`
  margin: 0;
  font-weight: 600;
`;

const Description3 = styled.p`
  margin: 0;
`;

const ConfirmSection = styled.div`
  border: 1px solid ${({ theme }) => theme.error};
  border-radius: 6px;
  padding: 8px;

  display: flex;
  align-items: baseline;
  gap: 20px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const formatStrings = {
  header: {
    en: "Permanently Remove Account?",
  },

  description1: {
    en:
      "Removing your account will result in the permanent erasure of your modules, " +
      "data, and billing details forever.",
  },
  description2: {
    en: "If you proceed, your login will be deactivated immediately.",
  },
  description3: {
    en: "To confirm, enter {email} below.",
  },

  confirmLabel: {
    en: "Confirmation:",
  },
  confirmPlaceholder: {
    en: "Enter your email to confirm",
  },
  confirmError: {
    en: "Entered text does not match: {email}",
  },

  removeLabel: {
    en: "Confirm Account Removal",
  },
  cancelLabel: {
    en: "Cancel",
  },
};
